import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { COLUMN_MAPPING_OPTIONS } from "../../api/columnMappingConstants"
import type {
  AnalyzedFile,
  ColumnEntry,
  ColumnOverride,
  CommitResult,
  MappingOverridePayload,
  SkippedComplianceColumn,
  SupplierNumberTwin,
  TargetTable,
} from "../../api/importsApi"
import {
  ENTITY_META,
  type EntityGroup,
  type EntityGroupType,
} from "../../api/ingestionApi"

/** Minimal column shape shared by the mapping list and the live preview. */
export interface MappingColumn {
  columnName: string
  /** Excel-style column letter — unique only *within* `sheetName`, not file-wide. */
  columnRef: string
  /** 0-based spreadsheet column index — used to index into `StagedRow.values`. */
  columnIndex: number
  /** Worksheet this column belongs to; `null` for CSVs / single-sheet files. */
  sheetName: string | null
  aiAssigned: string | null
  sampleValues: string[]
}

// ─── Analyzed-file → UI transforms ──────────────────────────────────────────
//
// The mapping screen fetches its own data via `useAnalyzedFiles()` and shapes
// the raw `AnalyzedFile` response into the structures the UI speaks.

const ENTITY_GROUP_TYPES = Object.keys(ENTITY_META) as EntityGroupType[]

function isEntityGroupType(value: string): value is EntityGroupType {
  return (ENTITY_GROUP_TYPES as string[]).includes(value)
}

const VALID_TARGET_TABLES = new Set(COLUMN_MAPPING_OPTIONS.map((t) => t.table))

/** `table` → set of its field values flagged `primary_key: true`. */
const PK_FIELDS_BY_TABLE: Record<string, Set<string>> = Object.fromEntries(
  COLUMN_MAPPING_OPTIONS.map((table) => [
    table.table,
    new Set(table.fields.filter((f) => f.primary_key).map((f) => f.value)),
  ]),
)

/**
 * The assignment a column gets when the user chooses to leave it out of the
 * import. Kept apart from `null`, which means "no target yet": the analyzer
 * also sends `null` for a column it could not map, and that column still needs
 * a decision, while a skipped one does not. On the wire both become
 * `{ targetTable: null }` (see `buildOverridePayload`). "skip" is not a table
 * in COLUMN_MAPPING_OPTIONS, so it never clashes with a real target.
 */
export const SKIPPED_ASSIGNMENT = "skip"

/** How many of a set of resolved assignments are mapped, skipped, or still open. */
export function countAssignments(assignments: (string | null)[]): {
  mapped: number
  skipped: number
  unmapped: number
} {
  let mapped = 0
  let skipped = 0
  for (const assignment of assignments) {
    if (assignment === SKIPPED_ASSIGNMENT) skipped++
    else if (assignment) mapped++
  }
  return { mapped, skipped, unmapped: assignments.length - mapped - skipped }
}

/** Whether an assignment string (`"table:field"`) targets a primary-key field. */
export function isPrimaryKeyAssignment(assignment: string | null): boolean {
  if (!assignment) return false
  const [table, field] = assignment.split(":")
  if (!field) return false
  return PK_FIELDS_BY_TABLE[table]?.has(field) ?? false
}

/**
 * THE IN-MEMORY key `columnOverrides[fileId]` uses for one column. A bare
 * header collides across sheets that share it (a distributor workbook's
 * "Teilenr" is the article number on one sheet and the product number on
 * another), which is what made those rows impossible to correct separately.
 *
 * Joined with NUL, which cannot occur in a sheet name or a column header, so
 * the key is injective: joining with a space is not, and sheet "A" + header
 * "B C" would share a key with sheet "A B" + header "C", quietly putting two
 * different columns back in one bucket. NUL stays inside the browser;
 * `buildOverridePayload` builds the readable, storable key for the wire, which
 * Postgres would reject with a NUL in it (JSONB forbids \u0000).
 */
const KEY_SEPARATOR = "\u0000"

export function scopedColumnKey(
  sheetName: string | null,
  columnName: string,
): string {
  return sheetName ? `${sheetName}${KEY_SEPARATOR}${columnName}` : columnName
}

/** Spreadsheet-style column letter for a zero-based index (0→A, 25→Z, 26→AA). */
export function columnIndexToRef(index: number): string {
  let ref = ""
  let n = index
  do {
    ref = String.fromCharCode(65 + (n % 26)) + ref
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return ref
}

/**
 * Collapse an analyzed column's target into the assignment string the mapping
 * UI speaks (`"articles"` / `"articles:supplier_article_number"`). Any table
 * present in COLUMN_MAPPING_OPTIONS is valid — this includes junction tables
 * like `mappings`, `product_articles`, and `additional_attributes`.
 */
function entryToAssignment(entry: ColumnEntry): string | null {
  const table = entry.targetTable
  if (!table || !VALID_TARGET_TABLES.has(table)) return null
  return entry.targetField ? `${table}:${entry.targetField}` : table
}

/** Normalise an entry's `sampleValues` (which may be absent/null) to strings. */
function sampleStrings(entry: ColumnEntry): string[] {
  return (entry.sampleValues ?? []).map((v) =>
    v === null || v === undefined ? "" : String(v),
  )
}

/** The ordered column list inside a file's mapping (defensive against nulls). */
function fileColumns(file: AnalyzedFile): ColumnEntry[] {
  return file.columnMapping?.columns ?? []
}

/**
 * The file's worksheets that actually have mappable columns, in sheet order,
 * with AI-skipped sheets removed. Files with no sheet info (CSVs, or files
 * predating multi-sheet support) return an empty array — callers treat that
 * as "single implicit sheet" and hide any sheet-specific UI.
 */
export function visibleSheetNames(file: AnalyzedFile): string[] {
  const names = file.sheetNames ?? []
  if (names.length === 0) return []
  const skipped = new Set((file.skippedSheets ?? []).map((s) => s.sheetName))
  return names.filter((name) => !skipped.has(name))
}

/**
 * Map one analyzed file's columns into ordered `MappingColumn` rows, grouped
 * by worksheet (in `sheetOrder`) and then by column index within each sheet.
 * Columns whose `sourceSheet` isn't in `sheetOrder` (e.g. CSVs, where it's
 * `null`) are appended last, in column-index order.
 */
export function analyzedFileColumns(file: AnalyzedFile): MappingColumn[] {
  const sheetOrder = visibleSheetNames(file)
  const sheetRank = (sheetName: string | null) => {
    if (sheetName === null) return sheetOrder.length
    const index = sheetOrder.indexOf(sheetName)
    return index === -1 ? sheetOrder.length : index
  }
  return [...fileColumns(file)]
    .sort((a, b) => {
      const rankDiff = sheetRank(a.sourceSheet) - sheetRank(b.sourceSheet)
      if (rankDiff !== 0) return rankDiff
      return (a.columnIndex ?? 0) - (b.columnIndex ?? 0)
    })
    .map((entry) => ({
      columnName: entry.sourceColumn,
      columnRef: columnIndexToRef(entry.columnIndex ?? 0),
      columnIndex: entry.columnIndex ?? 0,
      sheetName: entry.sourceSheet,
      aiAssigned: entryToAssignment(entry),
      sampleValues: sampleStrings(entry),
    }))
}

/** One worksheet's columns, in the order they should render. */
export interface ColumnSheetGroup {
  sheetName: string | null
  columns: MappingColumn[]
}

/**
 * Group an already sheet-ordered `MappingColumn[]` (as returned by
 * `analyzedFileColumns`) into per-sheet buckets, preserving order. Columns
 * are expected to already be contiguous per sheet, so this is a single
 * linear pass rather than a full group-by.
 */
export function groupColumnsBySheet(
  columns: MappingColumn[],
): ColumnSheetGroup[] {
  const groups: ColumnSheetGroup[] = []
  for (const column of columns) {
    const last = groups[groups.length - 1]
    if (last && last.sheetName === column.sheetName) {
      last.columns.push(column)
    } else {
      groups.push({ sheetName: column.sheetName, columns: [column] })
    }
  }
  return groups
}

/**
 * Reconstruct an entity's sample rows from the column-level `sampleValues`.
 * Within a single file every column that targets the same entity is row-aligned
 * by sample index, so we zip those columns into `{ field: value }` rows (keyed
 * by `targetField`, falling back to the source column). Rows from multiple files
 * are concatenated.
 */
function entitySamples(
  files: AnalyzedFile[],
  type: EntityGroupType,
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = []
  for (const file of files) {
    const cols = fileColumns(file).filter((entry) => entry.targetTable === type)
    if (!cols.length) continue
    const rowCount = cols.reduce(
      (max, entry) => Math.max(max, entry.sampleValues?.length ?? 0),
      0,
    )
    for (let i = 0; i < rowCount; i++) {
      const row: Record<string, unknown> = {}
      for (const entry of cols) {
        row[entry.targetField ?? entry.sourceColumn] =
          entry.sampleValues?.[i] ?? ""
      }
      rows.push(row)
    }
  }
  return rows
}

/**
 * Sum `entitiesToWrite` across all analyzed files into the `EntityGroup` shape
 * the review screen renders, attaching reconstructed sample rows per entity.
 * Confidence/status aren't part of the analyze response, so they default — the
 * analyze step already vetted the mapping, so every surfaced group is ready.
 */
export function analyzedFilesToGroups(files: AnalyzedFile[]): EntityGroup[] {
  const counts: Partial<Record<EntityGroupType, number>> = {}
  for (const file of files) {
    for (const [type, count] of Object.entries(file.entitiesToWrite ?? {})) {
      if (!isEntityGroupType(type)) continue
      counts[type] = (counts[type] ?? 0) + count
    }
  }
  return ENTITY_GROUP_TYPES.filter((type) => (counts[type] ?? 0) > 0).map(
    (type) => ({
      type,
      count: counts[type] ?? 0,
      confidence: 1,
      status: "ok" as const,
      samples: entitySamples(files, type),
    }),
  )
}

/**
 * Compliance columns the commit will skip, across all analyzed files, for the
 * review screen's notice. Same source as `analyzedFilesToGroups` (the counts it
 * sits next to), deduped by column + regulation like the commit's own warnings,
 * so a column repeated in two files is named once.
 */
export function analyzedFilesToSkippedComplianceColumns(
  files: AnalyzedFile[],
): SkippedComplianceColumn[] {
  const seen = new Set<string>()
  const out: SkippedComplianceColumn[] = []
  for (const file of files) {
    for (const col of file.skippedComplianceColumns ?? []) {
      const key = `${col.column}\u0000${col.framework ?? ""}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(col)
    }
  }
  return out
}

/**
 * The set of target fields offered in the assignment dropdown, derived from the
 * fields the analyze step actually produced per entity (deduped, in first-seen
 * order). Keeps the dropdown in sync with the real `target_field` values rather
 * than a hardcoded list.
 */
export function analyzedFilesToEntityFields(
  files: AnalyzedFile[],
): Record<EntityGroupType, string[]> {
  const fields = Object.fromEntries(
    ENTITY_GROUP_TYPES.map((type) => [type, [] as string[]]),
  ) as Record<EntityGroupType, string[]>
  for (const file of files) {
    for (const entry of fileColumns(file)) {
      const table = entry.targetTable
      if (!table || !isEntityGroupType(table) || !entry.targetField) continue
      if (!fields[table].includes(entry.targetField)) {
        fields[table].push(entry.targetField)
      }
    }
  }
  return fields
}

/**
 * Turn the mapping screen's per-file assignment overrides into the
 * `POST /files/{id}/confirm` body. The UI speaks `"table"` / `"table:field"`
 * assignment strings (or `null` / `SKIPPED_ASSIGNMENT` to skip); the API wants
 * `{ targetTable, targetField }`, and to skip a column an explicit
 * `{ targetTable: null }` object (NOT a bare `null`). Role is omitted — the
 * backend re-infers it.
 *
 * `fileOverrides` is keyed by `scopedColumnKey` (T1). `columns` (the file's
 * own `MappingColumn[]`) resolves each key back to its `sheetName`, which is
 * sent alongside as `sourceSheet`: the backend never splits the key itself
 * (sheet names may contain spaces), so the override object carries the sheet
 * explicitly. A key with no matching column (e.g. the file was re-analyzed
 * and the column is gone) is sent as-is, sourceSheet omitted; the backend
 * then finds no column for it either, the same silent no-op a stale legacy
 * override already was.
 */
export function buildOverridePayload(
  fileOverrides: Record<string, string | null>,
  columns: MappingColumn[],
): MappingOverridePayload {
  const columnByKey = new Map(
    columns.map((c) => [scopedColumnKey(c.sheetName, c.columnName), c]),
  )
  const overrides: Record<string, ColumnOverride> = {}
  for (const [key, assignment] of Object.entries(fileOverrides)) {
    const column = columnByKey.get(key)
    // A key with no column behind it is a leftover: a re-analysis renamed or
    // dropped that column while the override survived in component state. The
    // backend no-ops it (no column matches), but sending it is still harmful,
    // because the key would be the in-memory NUL-joined form and confirm merges
    // every key it receives into mapping['user_overrides'], which is JSONB, and
    // Postgres refuses \u0000 inside JSONB: a dead override would take the whole
    // confirm down with a 500. Dropping it here loses nothing.
    if (!column) continue
    const sheetName = column.sheetName
    // The wire key is the readable `"<sheet> <header>"` form, which is what
    // lands in the audit trail. It is NOT what the backend matches on:
    // `sourceSheet` and `sourceColumn` below say exactly which column this is,
    // so the key never has to be parsed back apart. Where two columns would
    // nevertheless produce the SAME wire key, the second is suffixed, because
    // one dict entry silently overwriting another is the bug this whole task
    // exists to remove.
    let wireKey = sheetName ? `${sheetName} ${column.columnName}` : key
    if (wireKey in overrides) wireKey = `${wireKey} #${column.columnIndex}`
    const scope = {
      ...(sheetName ? { sourceSheet: sheetName } : {}),
      sourceColumn: column.columnName,
    }
    if (assignment === null || assignment === SKIPPED_ASSIGNMENT) {
      overrides[wireKey] = { targetTable: null, ...scope }
      continue
    }
    const [table, field] = assignment.split(":")
    overrides[wireKey] = {
      targetTable: table as TargetTable,
      targetField: field ?? null,
      ...scope,
    }
  }
  return { overrides }
}

/**
 * Sum one or more per-entity count records (e.g. every file's
 * `recordsCreated`) into a single zero-filled `Record<EntityGroupType,
 * number>` — the shape `EntityScoreboard` renders. Keys outside
 * `ENTITY_GROUP_TYPES` (junction tables like `product_articles`) are ignored.
 */
export function sumEntityCounts(
  records: (Record<string, number> | null | undefined)[],
): Record<EntityGroupType, number> {
  const counts = Object.fromEntries(
    ENTITY_GROUP_TYPES.map((type) => [type, 0]),
  ) as Record<EntityGroupType, number>
  for (const record of records) {
    for (const [type, count] of Object.entries(record ?? {})) {
      if (!isEntityGroupType(type)) continue
      counts[type] += count ?? 0
    }
  }
  return counts
}

/**
 * Aggregate created + updated record counts across all committed files into the
 * `EntityGroup` shape the committed summary screen renders.
 */
export function commitResultsToGroups(results: CommitResult[]): EntityGroup[] {
  const written: Partial<Record<EntityGroupType, number>> = {}
  for (const result of results) {
    for (const record of [result.recordsCreated, result.recordsUpdated]) {
      for (const [type, count] of Object.entries(record ?? {})) {
        if (!isEntityGroupType(type)) continue
        written[type] = (written[type] ?? 0) + (count ?? 0)
      }
    }
  }
  return ENTITY_GROUP_TYPES.filter((type) => (written[type] ?? 0) > 0).map(
    (type) => ({
      type,
      count: written[type] ?? 0,
      confidence: 1,
      status: "ok" as const,
    }),
  )
}

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  id: "ID",
  vat: "VAT",
  cas: "CAS",
  rohs: "RoHS",
  svhc: "SVHC",
  reach: "REACH",
}

function formatField(field: string) {
  return (
    FIELD_LABEL_OVERRIDES[field] ??
    field.charAt(0).toUpperCase() + field.slice(1)
  )
}

/**
 * Turns an assignment value (`"articles"` or `"articles:id"`) into a human
 * label like `"Articles · attribute"` / `"Articles · ID"`. Returns `null` for
 * unassigned columns so callers can render their own placeholder.
 *
 * Pure utility (no `useTranslation` access), so callers pass their own
 * `translate` — see `MasterDataMappingModule`'s `formatTargetLabel` wiring.
 */
export function formatAssignmentLabel(
  assignment: string | null,
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string,
): string | null {
  if (!assignment) return null
  if (assignment === SKIPPED_ASSIGNMENT) {
    return translate("MC_MD_MAP_SKIPPED_LABEL")
  }
  const [type, field] = assignment.split(":") as [EntityGroupType, string?]
  // Junction tables (product_articles, compliance, …) have no ENTITY_META
  // entry — their labels live only on COLUMN_MAPPING_OPTIONS.
  const tableOption = COLUMN_MAPPING_OPTIONS.find((t) => t.table === type)
  const label = tableOption
    ? translate(tableOption.labelKey)
    : ENTITY_META[type]
      ? translate(ENTITY_META[type].labelKey)
      : type
  if (!field) return translate("MC_MD_MAP_TARGET_ATTRIBUTE_SUFFIX", { label })
  const fieldOption = tableOption?.fields.find((f) => f.value === field)
  const fieldLabel = fieldOption
    ? translate(fieldOption.labelKey)
    : formatField(field)
  return `${label} · ${fieldLabel}`
}

// ─── Column state colours ──────────────────────────────────────────────────────
//
// Four possible states:
//   "unmapped"  – no target assigned yet           → orange / warning
//   "skipped"   – user left it out of the import   → dimmed
//   "modified"  – user overrode the AI suggestion  → yellow / primary
//   "default"   – AI suggestion accepted as-is     → no accent
//
// Each shape bundles every class that all consumers need so they stay in sync
// when the palette changes.

export type ColumnState = "unmapped" | "skipped" | "modified" | "default"

/** Derive a column's state from its current assignment + original AI suggestion. */
export function getColumnState(
  assignment: string | null,
  aiAssigned: string | null,
): ColumnState {
  if (assignment === null) return "unmapped"
  if (assignment === SKIPPED_ASSIGNMENT) return "skipped"
  if (assignment !== aiAssigned) return "modified"
  return "default"
}

/** Dims a skipped column, in the column list and in the preview alike. */
export const SKIPPED_COLUMN_CLASS = "opacity-60"

/** Tailwind classes for a Select trigger (border + background + text). */
export const COLUMN_STATE_TRIGGER: Record<ColumnState, string> = {
  unmapped: "border-warning bg-warning/10 text-warning",
  skipped: "text-muted-foreground",
  modified: "border-primary bg-primary/10",
  default: "",
}

/** Tailwind classes for a table header cell (background tint). */
export const COLUMN_STATE_HEADER_BG: Record<ColumnState, string> = {
  unmapped: "bg-warning/5",
  skipped: "",
  modified: "",
  default: "",
}

/** Tailwind classes for a data cell (background tint). */
export const COLUMN_STATE_CELL_BG: Record<ColumnState, string> = {
  unmapped: "bg-warning/3",
  skipped: "",
  modified: "",
  default: "",
}

/** Tailwind classes for the Excel letter chip text colour. */
export const COLUMN_STATE_LETTER: Record<ColumnState, string> = {
  unmapped: "text-warning",
  skipped: "",
  modified: "",
  default: "",
}

/** Every file's supplier-number twins, one entry per incoming number. */
export function analyzedFilesToSupplierNumberTwins(
  files: AnalyzedFile[],
): SupplierNumberTwin[] {
  const byIncoming = new Map<string, SupplierNumberTwin>()
  for (const file of files) {
    for (const twin of file.supplierNumberTwins ?? []) {
      if (!byIncoming.has(twin.incoming)) byIncoming.set(twin.incoming, twin)
    }
  }
  return [...byIncoming.values()]
}
